import { useMemo } from 'react';

import useAuth from '@/hooks/useAuth';

import { buildGuestStreakState } from '../helpers/guestStreakHelpers';
import { deriveStreakState, HIDDEN_STATE } from '../helpers/streakHelpers';
import useActivePromoQuery from './useActivePromoQuery';
import useGuestLadderQuery from './useGuestLadderQuery';
import usePromoItemsQuery from './usePromoItemsQuery';

/** Stable identity so the guest branch can't churn consumers' effect deps. */
const noRefetch = () => {};

/**
 * The streak feature's read model — two branches behind one shape. A signed-in
 * user gets the promo + items queries; a guest gets the promo plus an
 * anonymous read of the same items endpoint, used for its reward ladder only
 * (day 1 always, nothing persisted).
 *
 * The branch is chosen on `isAuthorized`, never on absent data. That is what
 * rules out a wrong-state flash either way: a freshly registered user's promo
 * query is keyed by a new user id, so it starts empty and the feature is
 * briefly absent rather than claiming day 1.
 *
 * `isReady` is true once the queries this branch needs have settled — auto-open
 * and visibility decisions must wait for it.
 */
const useStreakState = () => {
  const { isAuthorized } = useAuth();
  const isGuest = !isAuthorized;

  const promoQuery = useActivePromoQuery();
  const promo = promoQuery.data ?? null;
  // Each branch withholds the promo id from the other's query, so only one of
  // the two items reads is ever in flight.
  const itemsQuery = usePromoItemsQuery(isGuest ? undefined : promo?.id);
  const guestLadderQuery = useGuestLadderQuery(isGuest ? promo?.id : undefined);

  const streak = useMemo(() => {
    if (!isGuest) return deriveStreakState(promo, itemsQuery.data);
    // Stay hidden until the ladder read settles — success or 401 — so a guest
    // never watches the amounts appear or change under them.
    if (guestLadderQuery.isPending) return HIDDEN_STATE;

    return buildGuestStreakState({
      promo,
      itemsResponse: guestLadderQuery.data,
    });
  }, [
    isGuest,
    promo,
    itemsQuery.data,
    guestLadderQuery.data,
    guestLadderQuery.isPending,
  ]);

  const isReady =
    !promoQuery.isPending &&
    (promo === null ||
      !(isGuest ? guestLadderQuery.isPending : itemsQuery.isPending));

  return {
    streak,
    promo,
    isGuest,
    isReady,
    refetchItems: isGuest ? noRefetch : itemsQuery.refetch,
  };
};

export default useStreakState;
